import { useCallback, useEffect, useRef, useState } from "react";
import type { RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import { listNotifications, markAllRead, markRead } from "../services/api";
import { setTokenProvider, subscribeToHub } from "../services/hub";
import type { Notification } from "../types";

export interface UseNotifications {
  notifications: Notification[];
  unreadCount: number;
  /** True once the live SignalR connection is established. */
  connected: boolean;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  /** Re-fetch the inbox from the API. */
  refresh: () => void;
}

const MAX_ITEMS = 100;

/**
 * Highest notification id this browser has already surfaced (live toast or a
 * seen inbox load). Ids are the inbox table's identity, so they only grow.
 *
 * Persisted because the pane is not always listening when a push lands: the
 * idle sign-out unmounts the subscribers, a closed pane has no runtime, and a
 * dropped socket can take minutes to re-negotiate. Those notifications reach the
 * inbox but never arrive as a live push, so without a watermark they would sit
 * silently in the bell. Holds only an integer id — no message content or PII.
 */
const WATERMARK_KEY = "aiva.notifications.lastSurfacedId";
// Fallback when localStorage is unavailable (private window, blocked storage).
let memoryWatermark: number | null = null;

function readWatermark(): number | null {
  try {
    const raw = globalThis.localStorage?.getItem(WATERMARK_KEY);
    const parsed = raw == null ? Number.NaN : Number(raw);
    if (Number.isSafeInteger(parsed)) {
      return parsed;
    }
  } catch {
    // Storage blocked — fall through to the in-memory copy.
  }
  return memoryWatermark;
}

function advanceWatermark(id: number): void {
  const current = readWatermark();
  if (current !== null && current >= id) {
    return;
  }
  memoryWatermark = id;
  try {
    globalThis.localStorage?.setItem(WATERMARK_KEY, String(id));
  } catch {
    // Best-effort; the in-memory copy still dedupes for this runtime.
  }
}

/**
 * Unread inbox items that arrived after the watermark, oldest first — the
 * notifications that were missed while no live connection was listening. With
 * no watermark yet (first run on this browser) nothing is returned, so existing
 * history isn't replayed as a burst of pop-ups.
 */
function missedSince(items: readonly Notification[], watermark: number | null): Notification[] {
  if (watermark === null) {
    return [];
  }
  return items.filter((n) => !n.read && n.id > watermark).sort((a, b) => a.id - b.id);
}

/** Test-only: forget the in-memory watermark between cases. */
export function resetNotificationWatermarkForTests(): void {
  memoryWatermark = null;
}

/**
 * Loads the notification inbox (offline catch-up) and subscribes to live
 * pushes over SignalR. Live deliveries and fetched rows are merged with
 * id-based dedup, so a push that races the initial fetch is shown once.
 *
 * `onNotification` fires for every live push and also for unread inbox items
 * that were missed while nothing was listening (see {@link missedSince}), so a
 * notification sent while the pane was idle, signed out or reconnecting still
 * pops up when the pane next loads the inbox.
 *
 * The token is read through a ref so the SignalR connection isn't torn down
 * and rebuilt on every token refresh.
 */
export function useNotifications(
  token: string | null,
  refreshTokens?: RefreshTokenFn,
  onNotification?: (n: Notification) => void,
): UseNotifications {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);

  const tokenRef = useRef(token);
  tokenRef.current = token;
  const refreshRef = useRef(refreshTokens);
  refreshRef.current = refreshTokens;
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;
  // Coalesce concurrent inbox fetches: mount fires several refresh() sources at
  // once (initial-fetch effect + the hub's immediate onState + the connect
  // event, times any transient extra subscribers), and they'd otherwise each
  // hit the inbox API. While one load is in flight, others mark a single trailing
  // re-run instead of issuing their own request.
  const loadingRef = useRef(false);
  const pendingLoadRef = useRef(false);
  // A load can settle after this instance unmounted (the add-in mounts its tree
  // more than once). Only the mounted instance may surface missed items, or a
  // discarded instance would consume the watermark and the visible one would
  // show nothing.
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const recompactUnread = useCallback((items: Notification[]) => {
    setUnreadCount(items.filter((n) => !n.read).length);
  }, []);

  /** Merge an incoming notification, deduping on id and capping the list. */
  const ingest = useCallback(
    (incoming: Notification) => {
      setNotifications((prev) => {
        if (prev.some((n) => n.id === incoming.id)) {
          return prev;
        }
        const next = [incoming, ...prev].slice(0, MAX_ITEMS);
        recompactUnread(next);
        return next;
      });
      advanceWatermark(incoming.id);
      onNotificationRef.current?.(incoming);
    },
    [recompactUnread],
  );

  /** Pop up anything the inbox holds that no live push delivered, then move the watermark past it. */
  const surfaceMissed = useCallback((items: readonly Notification[]) => {
    const watermark = readWatermark();
    for (const n of missedSince(items, watermark)) {
      onNotificationRef.current?.(n);
    }
    const newest = items.reduce((max, n) => Math.max(max, n.id), 0);
    if (watermark === null || newest > watermark) {
      advanceWatermark(newest);
    }
  }, []);

  const load = useCallback(async () => {
    if (loadingRef.current) {
      // A fetch is already running; ask it to re-run once instead of piling on.
      pendingLoadRef.current = true;
      return;
    }
    loadingRef.current = true;
    try {
      do {
        pendingLoadRef.current = false;
        const inbox = await listNotifications(tokenRef.current, { take: MAX_ITEMS }, refreshRef.current); // NOSONAR the reload loop re-fetches until no refresh is pending
        setNotifications(inbox.items);
        setUnreadCount(inbox.unreadCount);
        if (mountedRef.current) {
          surfaceMissed(inbox.items);
        }
      } while (pendingLoadRef.current);
    } catch {
      // Inbox fetch is best-effort; live pushes still populate the list.
    } finally {
      loadingRef.current = false;
    }
  }, [surfaceMissed]);

  const refresh = useCallback(() => {
    load().catch(() => {});
  }, [load]);

  // Initial inbox fetch.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Live connection. Subscribes to the process-wide shared hub rather than
  // building a per-instance connection: the add-in mounts its tree multiple
  // times (StrictMode + host bootstrap), and a per-effect socket leaves the
  // winning connection bound to an unmounted instance — frames then update dead
  // state while the visible instance has no socket. The shared hub fans every
  // frame out to all live subscribers, so this instance always receives them.
  useEffect(() => {
    setTokenProvider(async () => {
      const refreshed = await refreshRef.current?.();
      return refreshed ?? tokenRef.current ?? "";
    });

    return subscribeToHub(
      (n) => ingest(n),
      (isConnected) => {
        setConnected(isConnected);
        if (isConnected) {
          // On (re)connect, re-sync the inbox to catch anything missed while down.
          refresh();
        }
      },
    );
  }, [ingest, refresh]);

  const markAsRead = useCallback(async (id: number) => {
    // Optimistic; the server is the source of truth on next fetch.
    setNotifications((prev) => {
      const next = prev.map((n) => (n.id === id ? { ...n, read: true } : n));
      setUnreadCount(next.filter((n) => !n.read).length);
      return next;
    });
    try {
      await markRead(id, tokenRef.current, refreshRef.current);
    } catch {
      refresh(); // reconcile on failure
    }
  }, [refresh]);

  const markAllAsRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await markAllRead(tokenRef.current, refreshRef.current);
    } catch {
      refresh();
    }
  }, [refresh]);

  return { notifications, unreadCount, connected, markAsRead, markAllAsRead, refresh };
}
